'use client'

import { Cell, Image, Text } from '@vinted/web-ui'
import { InView } from 'react-intersection-observer'

import {
  DynamicFilterOptionModel,
  OptionExtension,
  OptionExtentionType,
  PrefixImageSize,
} from '@marketplace-web/catalog/catalog-data'

import ColorBubble from '../ColorBubble'
import { SelectableListItem } from '../../../SelectableItemList'

type Props = {
  type: string
  item: SelectableListItem<DynamicFilterOptionModel, string | number>
  itemElementProps?: React.ComponentProps<typeof Cell>
  handleItemView: (inView: boolean, entry: IntersectionObserverEntry) => void
}

const DynamicFilterCell = ({ type, item, itemElementProps = {}, handleItemView }: Props) => {
  const getPrefixSize = (size?: string) => {
    switch (size) {
      case PrefixImageSize.Medium: {
        return Image.Size.Medium
      }
      case PrefixImageSize.Regular: {
        return Image.Size.Regular
      }
      default: {
        return Image.Size.Regular
      }
    }
  }

  const renderPrefix = (id: number | string, prefix?: OptionExtension) => {
    if (!prefix) return null

    switch (prefix.type) {
      case OptionExtentionType.Color: {
        return prefix.code ? (
          <ColorBubble code={prefix.code} testId={`color_code_${prefix.code}`} />
        ) : null
      }
      case OptionExtentionType.Image: {
        return prefix.url ? (
          <Image
            src={prefix.url}
            scaling={Image.Scaling.Cover}
            size={getPrefixSize(prefix.size)}
            testId={`image_id_${id}`}
          />
        ) : null
      }
      default:
        return null
    }
  }

  const { key: cellKey, ...restItemElementProps } = itemElementProps as typeof itemElementProps & {
    key?: string
  }

  return (
    <InView onChange={handleItemView}>
      <Cell
        key={cellKey}
        {...restItemElementProps}
        title={<Text text={item.title} type={Text.Type.Title} as="span" />}
        body={item.data?.description ? item.data?.description : null}
        prefix={renderPrefix(item.id, item.data?.prefix)}
        testId={`selectable-item-${type}-${item.id}`}
      />
    </InView>
  )
}

export default DynamicFilterCell
